import { useEffect, useState } from "react";
import { Navigate, Outlet, useSearchParams } from "react-router";
import { DesignSkeleton } from "@/components/design-switcher/skeleton";
import {
  needsReaderSelection,
  readerIdOf,
  type SessionStore,
  sessionStore,
  useSession,
} from "@/convex/session";
import { GATE_PARAM, VIA_PARAM } from "@/contracts/session";
import { BookmarkIdentityGate } from "@/library/bookmark-identity-gate";
import { readNickname } from "@/storage/nickname";
import {
  isUnconfirmed,
  landingGate,
  markUnconfirmed,
} from "@/storage/unconfirmed-reader";

export const SELECT_READER_PATH = "/select-reader";

// The Worker has already switched the session when a bookmark lands, so
// the gate guards every reader route, not just the page the scan opened:
// a reload, a new tab or a typed /reader/<id> must ask too. The landing
// names its reader, so the marker is written before the session answers:
// a failed read on landing must not let a reload past the gate. The
// landing then leaves history, so Back cannot replay it.
function useUnconfirmedReader(readerId: string | null): {
  unconfirmed: boolean;
  confirm: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const gate = landingGate(searchParams);
  const [confirmedReader, setConfirmedReader] = useState<string | null>(null);

  useEffect(() => {
    if (gate === null) return;
    markUnconfirmed(gate, Date.now());
    setSearchParams(
      (params) => {
        params.delete(VIA_PARAM);
        params.delete(GATE_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [gate, setSearchParams]);

  const unconfirmed =
    readerId !== null &&
    confirmedReader !== readerId &&
    (gate === readerId || isUnconfirmed(readerId));
  return { unconfirmed, confirm: () => setConfirmedReader(readerId) };
}

// A signed-out or unreachable session still renders the route: its queries
// answer null for no reader, and a reader on a dead connection keeps reading.
export function RequireReader({
  store = sessionStore,
}: {
  store?: SessionStore;
}) {
  const session = useSession(store);
  const readerId = readerIdOf(session);
  const { unconfirmed, confirm } = useUnconfirmedReader(readerId);
  if (session.status === "loading") return <DesignSkeleton />;
  if (needsReaderSelection(session)) {
    return <Navigate to={SELECT_READER_PATH} replace />;
  }
  if (unconfirmed && readerId !== null) {
    return (
      <>
        <DesignSkeleton />
        <BookmarkIdentityGate
          readerId={readerId}
          nickname={readNickname(readerId)}
          onConfirmed={confirm}
        />
      </>
    );
  }
  return <Outlet />;
}
